import {
  AlertTriangle,
  CalendarClock,
  Clock,
  ExternalLink,
  Footprints,
  MapPin,
  MountainSnow,
  Route,
  TrendingUp,
} from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { Hike, UiLang } from "../lib/types";

interface Props {
  hike: Hike;
  lang: UiLang;
}

/**
 * Fotturen fram til stedet — tid, stigning, sesong og hvem som fører den.
 *
 * Panelet står øverst i detaljpanelet, over vær og bakgrunn, og det er et
 * bevisst valg: alt annet på et stopp er noe man kan lese etterpå, mens
 * «denne turen tar tolv timer» er noe man må vite før man planlegger dagen.
 * Tidsadvarselen står som en advarsel og ikke som en faktalinje, fordi det er
 * den opplysningen turister faktisk undervurderer — Trolltunga er 27 kilometer
 * inn i fjellet, og hvert år hentes uforberedte turgåere ned derfra.
 *
 * Bestillingsfristen står ved hver operatør. Den er et råd og ikke en ledig
 * plass: ingen av aktørene har åpent API for kapasitet, så tallet sier hvor
 * tidlig man bør være ute.
 */
export function HikePanel({ hike, lang }: Props) {
  const t = dictionaries[lang];

  const facts = [
    { icon: Clock, text: t.hikeRoundTrip(hike.hoursMin, hike.hoursMax) },
    { icon: Route, text: t.hikeDistance(hike.distanceKm) },
    { icon: TrendingUp, text: t.hikeAscent(hike.ascentM) },
    { icon: MountainSnow, text: t.hikeGradeName[hike.grade] },
  ];

  return (
    <section className={`hike hike--${hike.grade}`}>
      <h3 className="hike-title">
        <Footprints size={15} />
        {t.hikeTitle}
      </h3>

      <div className="hike-facts">
        {facts.map(({ icon: Icon, text }) => (
          <span className="hike-fact" key={text}>
            <Icon size={13} />
            {text}
          </span>
        ))}
      </div>

      {/* Tiden er det folk undervurderer. Den står som advarsel, ikke som tall. */}
      <div className="banner banner--warn hike-warning">
        <AlertTriangle size={15} />
        <span>{hike.timeWarning}</span>
      </div>

      <dl className="hike-rows">
        <div className="hike-row">
          <dt>
            <MapPin size={12} />
            {t.hikeTrailhead}
          </dt>
          <dd>{hike.trailhead}</dd>
        </div>
        <div className="hike-row">
          <dt>
            <CalendarClock size={12} />
            {t.hikeSeason}
          </dt>
          <dd>{hike.season}</dd>
        </div>
        <div className="hike-row">
          <dt>
            <Route size={12} />
            {t.hikeReachedBy}
          </dt>
          <dd>{hike.trailheadModes.map((mode) => t.modeName[mode]).join(", ")}</dd>
        </div>
      </dl>

      <h4 className="hike-guides-title">{t.hikeGuidesTitle}</h4>
      {hike.guides.length === 0 ? (
        <p className="hike-guides-none">{t.hikeGuidesNone}</p>
      ) : (
        <ul className="hike-guides">
          {hike.guides.map((guide) => (
            <li className="hike-guide" key={guide.website}>
              <span className="hike-guide-head">
                <a href={guide.website} target="_blank" rel="noreferrer">
                  <b>{guide.operator}</b> <ExternalLink size={10} />
                </a>
                <span className="hike-book">{t.hikeBookAhead(guide.bookAheadDays)}</span>
              </span>
              {guide.note && <small>{guide.note}</small>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
